import { BrandConfig } from '../../config/brandConfig';

export type MintMessagingIdentityTokenResponse = {
  identityToken: string;
  expiresAt: number;
};

/**
 * Minimal shape of the Salesforce Enhanced Web Chat (MIAW) / Agentforce Messaging bootstrap object.
 * Only the APIs this app uses are typed.
 */
export type SalesforceEmbeddedServiceBootstrap = {
  settings?: { language?: string; shouldMinimizeWindowOnNewTab?: boolean };
  userVerificationAPI?: {
    clearSession?: (shouldEndSession?: boolean) => Promise<void> | void;
    setIdentityToken?: (params: {
      identityTokenType: 'JWT';
      identityToken: string;
    }) => Promise<void> | void;
  };
  utilAPI?: {
    removeAllComponents?: () => void;
  };
};

/** Returns whichever Salesforce messaging bootstrap global is present on the page. */
export const getMessagingBootstrap = (): SalesforceEmbeddedServiceBootstrap | undefined =>
  window.embeddedservice_bootstrap ?? window.agentforce_messaging;

declare global {
  interface Window {
    embeddedservice_bootstrap?: SalesforceEmbeddedServiceBootstrap;
    agentforce_messaging?: SalesforceEmbeddedServiceBootstrap;
    initEmbeddedMessaging?: () => void;
    __embeddedMessagingReady?: boolean;
    __embeddedChatInitialized?: boolean;
    /** Bridge handler for onEmbeddedMessagingReady, stored so re-inits can replace it. */
    __embeddedChatMarkReady?: () => void;
  }
}

type SalesforceMessagingConfig = BrandConfig['salesforceEmbeddedMessaging'];

const nonEmpty = (value: string | undefined): boolean => Boolean(value?.trim());

/** When false, required org/channel/site/SCRT are missing and the widget must not bootstrap. */
export const isSalesforceEmbeddingConfigured = (m: SalesforceMessagingConfig): boolean =>
  nonEmpty(m.orgId) && nonEmpty(m.channelName) && nonEmpty(m.siteUrl) && nonEmpty(m.scrt2Url);

export const defaultBootstrapScriptSrc = (siteUrl: string, cacheBust?: number): string => {
  const base = `${siteUrl.replace(/\/$/, '')}/assets/js/bootstrap.min.js`;
  return cacheBust != null ? `${base}?_=${cacheBust}` : base;
};

/**
 * Builds the inline init script. `onEmbeddedMessagingReady` is bridged to a custom
 * `embeddedMessagingReadyForPrechat` event so React can apply the identity token once ready.
 * The event name is historical (pre-chat was removed); the payload is JWT only.
 * The bridge is intentionally NOT once-only: for verified users the SDK fires
 * `onEmbeddedMessagingReady` again after `clearSession()` on logout/re-init, and the
 * identity token must be re-applied.
 *
 * `shouldMinimizeWindowOnNewTab` must be set before `init()`. The SDK's own default (false)
 * reopens an in-progress conversation maximized over the page the guest just landed on.
 */
export const buildInitScript = (config: SalesforceMessagingConfig): string => {
  const language = JSON.stringify(config.language ?? 'en_US');
  const orgId = JSON.stringify(config.orgId);
  const channelName = JSON.stringify(config.channelName);
  const siteUrl = JSON.stringify(config.siteUrl);
  const scrt2URL = JSON.stringify(config.scrt2Url);
  const shouldMinimizeWindowOnNewTab = JSON.stringify(config.shouldMinimizeWindowOnNewTab ?? true);

  return `
window.initEmbeddedMessaging = function () {
  try {
    var boot = window.embeddedservice_bootstrap || window.agentforce_messaging;
    if (!boot) {
      console.error('Error loading Embedded Messaging: bootstrap global not found');
      return;
    }
    boot.settings.language = ${language};
    boot.settings.shouldMinimizeWindowOnNewTab = ${shouldMinimizeWindowOnNewTab};

    // Remove any bridge left over from a previous init (logout/login) to avoid duplicates.
    if (window.__embeddedChatMarkReady) {
      window.removeEventListener('onEmbeddedMessagingReady', window.__embeddedChatMarkReady);
    }
    window.__embeddedChatMarkReady = function embeddedChatMarkReady() {
      window.__embeddedMessagingReady = true;
      window.dispatchEvent(new CustomEvent('embeddedMessagingReadyForPrechat'));
    };
    window.addEventListener('onEmbeddedMessagingReady', window.__embeddedChatMarkReady);

    boot.init(
      ${orgId},
      ${channelName},
      ${siteUrl},
      { scrt2URL: ${scrt2URL} }
    );
  } catch (err) {
    console.error('Error loading Embedded Messaging: ', err);
  }
};
`.trim();
};

/**
 * Passes a User Verification JWT to Enhanced Chat. Must run after onEmbeddedMessagingReady.
 * Chat button renders only after a valid token is accepted (Auth-mode channels). Omni stamps
 * the Messaging End User from MessagingPlatformKey (`uid:{uuid}` on JWT `sub`).
 */
export const setMessagingIdentityToken = async (identityToken: string): Promise<boolean> => {
  const token = identityToken?.trim();
  const setToken = getMessagingBootstrap()?.userVerificationAPI?.setIdentityToken;
  if (!token || !setToken) {
    return false;
  }

  try {
    await setToken({
      identityTokenType: 'JWT',
      identityToken: token,
    });
    return true;
  } catch (err) {
    console.error('[embeddedMessagingSession] setIdentityToken failed: ', err);
    return false;
  }
};
